import _ from 'lodash';

import * as api from '../api';

///////////////////////////////////////////////////
////// CONSTS

const NON_DOCTOR_SUFFIX_SET = new Set<string>(
  Object.values(api.User.OtherSuffixMap),
);

///////////////////////////////////////////////////
////// TYPES

export type ProviderNameFields = Pick<
  api.User.UserIdentification,
  'firstName' | 'lastName' | 'prefix' | 'suffix'
>;

///////////////////////////////////////////////////
////// PUBLIC

// returns `Prefix. Firstname Lastname, SUFFIX`
export function getProviderNameFriendly(
  provider: ProviderNameFields,
  toInclude: ('firstName' | 'lastName' | 'prefix' | 'suffix')[],
): string {
  const { firstName, lastName, prefix, suffix } = provider;

  const pre: string[] = [];
  const post: string[] = [];

  if (toInclude.includes('prefix') && prefix) {
    pre.push(
      prefix.endsWith('.')
        ? wordsUpperFirst(prefix)
        : `${wordsUpperFirst(prefix)}.`,
    );
  }
  if (toInclude.includes('firstName') && firstName) {
    pre.push(wordsUpperFirst(firstName));
  }
  if (toInclude.includes('lastName') && lastName) {
    pre.push(wordsUpperFirst(lastName));
  }

  if (toInclude.includes('suffix') && suffix) {
    post.push(suffix.toUpperCase());
  }

  return [pre.join(' '), post.join(' ')].filter(Boolean).join(', ');
}

export function getProviderNameFull(args: {
  provider: ProviderNameFields;
  onlyFullName?: boolean;
}): string {
  const { provider, onlyFullName = false } = args;
  const { firstName, lastName, prefix, suffix } = provider;
  const fullName = [firstName, lastName].filter((x) => !!x).join(' ');
  if (onlyFullName || !fullName) return fullName;

  const providerSuffix = suffix ? `, ${suffix}` : '';
  if (!suffix || NON_DOCTOR_SUFFIX_SET.has(suffix.toUpperCase())) {
    const providerPrefix = prefix
      ? prefix.endsWith('.')
        ? `${prefix} `
        : `${prefix}. `
      : '';
    return `${providerPrefix}${fullName}${providerSuffix}`;
  }
  return `${fullName}${providerSuffix}`;
}

///////////////////////////////////////////////////
////// HELPERS

const wordsUpperFirst = (inputString: string): string => {
  const wordsUpperFirstFuncArray = [dashWordsUpperFirst, spaceWordsUpperFirst];
  return wordsUpperFirstFuncArray.reduce(
    (prevValue, upperFunction) => upperFunction(prevValue),
    inputString,
  );
};

const dashWordsUpperFirst = (inputString: string): string => {
  return delimiterWordsUpperFirst(inputString, '-');
};

const spaceWordsUpperFirst = (inputString: string): string => {
  return delimiterWordsUpperFirst(inputString, ' ');
};

const delimiterWordsUpperFirst = (
  inputString: string,
  delimiter: string,
): string => {
  return inputString
    .split(delimiter)
    .map((splitString) => _.upperFirst(splitString))
    .join(delimiter);
};
